import type { IntegrationEnhanced } from "@/api/integrations";
import { EntitySelectionProvider } from "@/components/EntitySelector/dnd/useEntitySelection";
import { NotImplementedSelector } from "@/components/EntitySelector/integrations/NotImplementedSelector";
import { getPlatformEntitySelector } from "@/constants/integration-platforms";
import type { IntegrationConnection } from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import { ErrorBoundary } from "@sentry/react";
import {
  INSERT_MENU_INTEGRATION_HEIGHT,
  INSERT_MENU_INTEGRATION_WIDTH,
} from "../primary/CanvasInsertMenu";
import { useTrayPortrait } from "./CanvasTrayContent";

export function buildConnectionForSelector(
  integration: IntegrationEnhanced,
): IntegrationConnection | null {
  if (!integration.integration_connection_id) {
    return null;
  }
  return {
    id: integration.integration_connection_id,
    integration_id: integration.id,
    integration_name: integration.name,
    integration_platform: integration.platform,
    integration_image_url: integration.metadata?.image_url,
    integration_provider: integration.provider,
    integration_provider_id: integration.provider_id,
    organization_id: "",
    user_id: "",
    status: "active",
    beta: false,
    config: integration.integration_connection_config || {},
    instructions: "",
    created_at: integration.created_at,
    updated_at: integration.updated_at,
  };
}

interface CanvasConnectorEntityBodyProps {
  integration: IntegrationEnhanced;
  onEntitySelect: (entity: Entity) => void;
}

/**
 * The entity picker a connected source opens — the tray's own body while
 * the tray is popped out (ROUND3 §2, D38): the frame takes the picker's
 * 680 × 520, the chrome names the connector and carries the back control,
 * and the picker browses inside. Escape, the chrome and the roster are the
 * frame's business. In portrait (beside the shed bar's column) it takes the
 * frame's width instead and the height the frame measured, at most its own,
 * and browses by scrolling.
 */
export function CanvasConnectorEntityBody({
  integration,
  onEntitySelect,
}: CanvasConnectorEntityBodyProps) {
  const portrait = useTrayPortrait();
  const connection = buildConnectionForSelector(integration);
  if (!connection) return null;
  // The platform selector itself, not `EntitySelector`: that wrapper draws
  // its own title row, and the chrome head already names the connector.
  const Selector =
    getPlatformEntitySelector(connection.integration_platform) ??
    NotImplementedSelector;
  return (
    <div
      className="flex min-h-0 flex-col"
      data-tray-picker={integration.id}
      style={
        portrait
          ? {
              width: "100%",
              height: `min(${INSERT_MENU_INTEGRATION_HEIGHT}px, var(--xtt-avail, ${INSERT_MENU_INTEGRATION_HEIGHT}px))`,
            }
          : {
              width: INSERT_MENU_INTEGRATION_WIDTH,
              height: INSERT_MENU_INTEGRATION_HEIGHT,
            }
      }
    >
      <EntitySelectionProvider key={connection.id}>
        <ErrorBoundary
          fallback={
            <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
              <p>Error loading connection</p>
            </div>
          }
        >
          <Selector
            key={connection.id}
            connection={connection}
            onSelect={onEntitySelect}
            className="h-full border-0"
          />
        </ErrorBoundary>
      </EntitySelectionProvider>
    </div>
  );
}
